import { useEffect, useState } from 'react';

export const useVersionTrigger = () => {
    const [isVersionPopupOpen, setIsVersionPopupOpen] = useState(false);
    const [lastTapTime, setLastTapTime] = useState(0);
    const [touchStartTime, setTouchStartTime] = useState(0);
    const [touchCount, setTouchCount] = useState(0);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check if user is currently typing in an input field
            const activeElement = document.activeElement;
            const isTypingInInput = activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.getAttribute('contenteditable') === 'true' ||
                activeElement.classList.contains('ProseMirror') // For rich text editors
            );

            // Don't trigger version popup if user is typing in an input field
            if (isTypingInInput) {
                return;
            }

            // Secret trigger: Shift + V (easy to remember)
            if (event.key === 'V' && event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
                console.log('Shift+V detected! Opening version popup');
                event.preventDefault();
                setIsVersionPopupOpen(true);
            }
            
            // Alternative triggers: Ctrl/Cmd + Shift + V or Ctrl/Cmd + Alt + V
            if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'V') {
                console.log('Ctrl/Cmd+Shift+V detected! Opening version popup');
                event.preventDefault();
                setIsVersionPopupOpen(true);
            }
            
            if ((event.ctrlKey || event.metaKey) && event.altKey && event.key === 'V') {
                console.log('Ctrl/Cmd+Alt+V detected! Opening version popup');
                event.preventDefault();
                setIsVersionPopupOpen(true);
            }
        };

        // 3-finger long press for mobile
        const handleTouchStart = (event: TouchEvent) => {
            if (event.touches.length === 3) {
                setTouchStartTime(Date.now());
                setTouchCount(3);
            }
        };

        const handleTouchEnd = (event: TouchEvent) => {
            if (touchCount === 3) {
                const touchDuration = Date.now() - touchStartTime;
                if (touchDuration >= 2000) { // 2 seconds
                    event.preventDefault();
                    setIsVersionPopupOpen(true);
                }
                setTouchCount(0);
                setTouchStartTime(0);
            }
        };

        const handleTouchCancel = () => {
            setTouchCount(0);
            setTouchStartTime(0);
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchend', handleTouchEnd, { passive: false });
        window.addEventListener('touchcancel', handleTouchCancel);
        
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('touchcancel', handleTouchCancel);
        };
    }, [touchCount, touchStartTime]);

    const handleVersionIndicatorTap = () => {
        const now = Date.now();
        const timeDiff = now - lastTapTime;
        
        if (timeDiff < 500) { // Within 500ms
            if (timeDiff < 100) { // Triple tap threshold
                setIsVersionPopupOpen(true);
                setLastTapTime(0);
                return;
            }
        }
        
        setLastTapTime(now);
    };

    const openVersionPopup = () => {
        setIsVersionPopupOpen(true);
    };
    
    const closeVersionPopup = () => {
        setIsVersionPopupOpen(false);
    };

    return {
        isVersionPopupOpen,
        openVersionPopup,
        closeVersionPopup,
        handleVersionIndicatorTap
    };
}; 